import { getPreferredPhone, useGetAllContacts, useGetContact } from "@api/contactService";
import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import { getBusinessLogo, useGetBusiness } from "@api/businessService";
import { formatUrl } from "@utils/formatUrl";
import { yesNo } from "@utils/yesNo";
import { ContextualHelp } from "@components/ContextualHelp";
import { useGetProperty } from "@api/propertyService";
import { FauxTable, FauxTableRow } from "@components/FauxTable";
import { MapModal } from "@components/MapModal";
import { formatCurrency } from "@utils/numbers";

/*
interface ShowData {
    label?: string;
    conditional?: Function|boolean;
    content?: Function|string;
};
*/

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').toUpperCase() : '';

    return (
    <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1.5}
        pt={0}
    >
        {data?.files?.[0] && (
        <Avatar
            size="lg"
            sx={{
                '--Avatar-size': '5rem',
                backgroundColor: '#fff',
            }}
            src={data.files[0]?.url}
        >
            {avatarInitials}
        </Avatar>
        )}
        <Box sx={{flex: 1}}>
            <Typography fontSize={'md'} fontWeight={'bold'}>{data.address}</Typography>
            {data.address_2 && <Typography fontSize={'sm'}>{data.address_2}</Typography>}
            {/*
            {data.owner && (<Box sx={{display: 'flex', gap: 1}}>
                <Typography fontSize={'sm'} fontWeight={'bold'}>owner</Typography>
                {' '}
                <ObjectPreview to={`/${data.owner_type == 'App\\Models\\Contact' ? 'contacts' : 'businesses'}/${data.owner.id}`} relative={true} object={data.owner}>
                    <Typography sx={{display: 'inline-block'}}>{data.owner.name}</Typography>
                </ObjectPreview>
            </Box>)}
            */}
        </Box>
    </Stack>
)
};

const dataToDisplay/*: readonly ShowData[]*/ = [
    {
        label: 'Owner',
        conditional: object => !!object.owner,
        content: (object) => (
            <ObjectPreview to={`/${object.owner_type == 'App\\Models\\Contact' ? 'contacts' : 'businesses'}/${object.owner.id}`} relative={true} object={object.owner}>
                <Typography sx={{display: 'inline-block'}}>{object.owner.name}</Typography>
            </ObjectPreview>
        ),
    },
    {
        label: 'Coordinates',
        conditional: object => !!object.latitude && !!object.longitude,
        content: (object) => <>
            {/*
            <FauxTable>
                <FauxTableRow title={'Lat'}>{object.latitude}</FauxTableRow>
                <FauxTableRow title={'Long'}>{object.longitude}</FauxTableRow>
            </FauxTable>
            */}
            {object.latitude}, {object.longitude}
            <br/>
            <MapModal position={{lat: object.latitude, lng: object.longitude}}/>
        </>,
    },
    {
        label: 'Parcel Number',
        conditional: object => !!object.parcel_number,
        content: 'parcel_number',
    },
    {
        label: 'GIS Link',
        conditional: object => !!object.gis_url,
        content: (object, label) => <a href={formatUrl(object.gis_url)} target="_blank">View</a>,
    },
    {
        label: 'Square Footage',
        conditional: object => !!object.square_footage,
        content: 'square_footage',
    },
    {
        label: '# of Floors',
        conditional: object => !!object.floors,
        content: 'floors',
    },
    {
        label: 'Land Use Type',
        conditional: object => !!object.land_use_id,
        content: (object) => (<>{object.land_use?.name}</>),
    },
    {
        label: <ContextualHelp slug="commercial_units">Commercial Units</ContextualHelp>,
        // label: 'Commercial Units', // todo: add helper
        conditional: object => !!object.commercial_units,
        content: 'commercial_units',
    },
    {
        label: <ContextualHelp slug="residential_units">Residential Units</ContextualHelp>,
        // label: 'Residential Units',
        conditional: object => !!object.residential_units,
        content: 'residential_units',
    },
    {
        label: 'Residential Only',
        conditional: object => !!object.residential_only,
        content: (object) => yesNo(object.residential_only),
    },
    {
        label: 'TIF',
        conditional: object => !!object?.tif?.name,
        content: (object) => object.tif.name,
    },
    {
        label: 'SSA/BID',
        conditional: object => !!object?.ssa?.name,
        content: (object) => object.ssa.name,
    },
    {
        label: 'Zoning',
        conditional: object => !!object?.zone?.name,
        content: (object) => object.zone.name,
    },
    {
        label: 'Enterprise Zone',
        conditional: object => !!object.enterprise,
        content: (object) => yesNo(object.enterprise),
    },
    {
        label: 'For Sale',
        conditional: object => !!object.for_sale,
        content: (object) => yesNo(object.for_sale),
    },
    {
        label: 'For Lease',
        conditional: object => !!object.for_lease,
        content: (object) => yesNo(object.for_lease),
    },
    {
        label: 'Asking Price',
        conditional: object => !!object.asking_price,
        content: (object) => formatCurrency(object.asking_price),
    },
    // total market value
    {
        label: 'Assessed Market Value',
        conditional: object => !!object.assessed_market_value,
        content: (object) => formatCurrency(object.assessed_market_value),
    },
    {
        label: 'Private Parking',
        conditional: object => !!object.private_parking,
        content: 'private_parking',
    },
    {
        label: 'Architect',
        conditional: object => !!object.architect,
        content: 'architect',
    },
    {
        label: 'Built',
        conditional: object => isValidDate(object.built_date),
        content: (object) => formatDate(object.built_date),
    },
    {
        label: 'Date Lost',
        conditional: object => isValidDate(object.lost_date),
        content: (object) => formatDate(object.lost_date),
    },
    {
        label: 'Reason Lost',
        conditional: object => !!object.lost_reason,
        content: 'lost_reason',
    },
    /*
    {
        label: 'Notes',
        conditional: object => object.notes && object.notes.trim().length,
        content: 'notes',
    },
    */
    {
        label: 'Demographics',
        conditional: object => !!object.is_veteran || !!object.is_minority || !!object.is_female || !!object.is_lgbt,
        content: (object) => (<>
            {!!object.is_veteran && <Typography sx={{display: 'block'}}>Veteran-owned</Typography>}
            {!!object.is_minority && <Typography sx={{display: 'block'}}>Minority-owned</Typography>}
            {!!object.is_female && <Typography sx={{display: 'block'}}>Woman-owned</Typography>}
            {!!object.is_lgbt && <Typography sx={{display: 'block'}}>LGBT-owned</Typography>}
        </>),
    },
];

export default function PropertyShow() {

    return (
        <ObjectShow
            query={useGetProperty}
            type={'property'}
            permissionType={'properties'}
            title={'Properties'}
            subtitleKey={'address'}
            color={'property'}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfo
                    title={'Property Info'}
                    tint={'property'}
                    fields={dataToDisplay}
                    cardHeader={<CardHeader/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'contacts'} />
                <ObjectRelationship type={'involvements'} />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'notes'} />
                <ObjectRelationship type={'registries'} />
                <ObjectRelationship type={'sales'} />
                <ObjectRelationship type={'files'} />
            </ObjectColumn>

            {/* divider here */}
            <ObjectColumn forceBreak/>

            <ObjectColumn>
                <ObjectRelationship type={'property_businesses'} column="businesses" />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'improvements'} />
            </ObjectColumn>

            <ObjectColumn full>
                <ObjectRelationship type={'property_units'} />
            </ObjectColumn>

        </ObjectShow>
    )
}
